import React, { useRef, useState } from "react";

// Fast, consistent hover delay — replaces the unpredictable native
// browser `title` tooltip (which can take 1s+ and varies by OS/browser).
const SHOW_DELAY = 120; // ms
const HIDE_DELAY = 0; // hide immediately so it never feels "stuck"

/**
 * Wrap any icon button with this to get a quick, custom hover tooltip.
 *
 * <IconTooltip label="Delete">
 *   <button className="av-action-btn av-action-btn--icon">
 *     <Icon icon="tabler:trash" width={15} />
 *   </button>
 * </IconTooltip>
 */
const IconTooltip = ({ label, children, position = "top", offset = 8 }) => {
  const [visible, setVisible] = useState(false);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);

  const clearTimers = () => {
    if (showTimer.current) clearTimeout(showTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
  };

  const handleEnter = () => {
    clearTimers();
    showTimer.current = setTimeout(() => setVisible(true), SHOW_DELAY);
  };

  const handleLeave = () => {
    clearTimers();
    if (HIDE_DELAY > 0) {
      hideTimer.current = setTimeout(() => setVisible(false), HIDE_DELAY);
    } else {
      setVisible(false);
    }
  };

  if (!label) return children;

  const isTop = position === "top";

  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      {children}
      <span
        role="tooltip"
        style={{
          position: "absolute",
          left: "50%",
          [isTop ? "bottom" : "top"]: `calc(100% + ${offset}px)`,
          transform: visible
            ? "translateX(-50%) translateY(0)"
            : `translateX(-50%) translateY(${isTop ? "4px" : "-4px"})`,
          background: "#1f2937",
          color: "#fff",
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1.3,
          padding: "5px 9px",
          borderRadius: 6,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
          zIndex: 1000,
          pointerEvents: "none",
          opacity: visible ? 1 : 0,
          visibility: visible ? "visible" : "hidden",
          transition: "opacity 0.1s ease, transform 0.1s ease, visibility 0.1s",
        }}
      >
        {label}
        <span
          style={{
            position: "absolute",
            [isTop ? "top" : "bottom"]: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            [isTop ? "borderTop" : "borderBottom"]: "5px solid #1f2937",
          }}
        />
      </span>
    </span>
  );
};

export default IconTooltip;
